// src/pages/ProjectDetail.jsx
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import Topbar from "../components/Topbar";
import UserAvatar from "../components/UserAvatar";
import {
  FaArrowLeft,
  FaFileInvoiceDollar,
  FaClipboardList,
  FaFileWord,
  FaFilePdf,
  FaExclamationTriangle,
  FaUserTie,
  FaUsers,
  FaPaperclip,
  FaExternalLinkAlt,
} from "react-icons/fa";
import { hasRole, PROJECT_REPORT_MANAGER_ROLES } from "../config/roles";

const ABSOLUTE_HOST = "https://dev.sliitleo.org";
const ROLES = ["Leader", "Chairperson", "Secretary", "Treasurer"];

/* ---------- helpers ---------- */
function readUserFromStorage() {
  try {
    const raw = JSON.parse(localStorage.getItem("user")) || {};
    return { name: "Unknown User", role: "Member", image: "/user.png", ...raw };
  } catch {
    return { name: "Unknown User", role: "Member", image: "/user.png" };
  }
}
const toJsonSafe = async (res) => {
  const text = await res.text();
  try { return JSON.parse(text); } catch { throw new Error(text.slice(0, 300)); }
};
const absImg = (p) => (p?.startsWith("http") ? p : `${ABSOLUTE_HOST}${p?.startsWith("/") ? "" : "/"}${p || ""}`);

// presentation-only: map a free-text project status to a shared .pill tone
function pillClassForStatus(status) {
  const s = String(status || "").toLowerCase();
  if (/(approved|complete|completed|done)/.test(s)) return "pill-success";
  if (/(pending|review|draft|new)/.test(s)) return "pill-warning";
  if (/(reject|cancel|block|fail)/.test(s)) return "pill-danger";
  return "pill-info";
}

/* =================================================================== */
export default function ProjectDetail() {
  const { id: routeId } = useParams();
  const navigate = useNavigate();
  const user = useMemo(() => readUserFromStorage(), []);
  const [projectId, setProjectId] = useState(Number(routeId) || 0);

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [project, setProject] = useState(null);

  // meta
  const [description, setDescription] = useState("");

  const [needs, setNeeds] = useState("");
  const [savingMeta, setSavingMeta] = useState(false);

  // annexures
  const [annexures, setAnnexures] = useState(
    Array.from({ length: 12 }, (_, i) => ({ slot: i + 1, caption: "", url: "" }))
  );
  const [savingAnnex, setSavingAnnex] = useState(false);

  // team
  const [team, setTeam] = useState(() =>
    ROLES.reduce((acc, r) => ({ ...acc, [r]: { sliitid: "", preview: null } }), {})
  );
  const [savingTeam, setSavingTeam] = useState(false);

  // Project Officer tile
  const [officerId, setOfficerId] = useState("");
  const [officer, setOfficer] = useState(null);
  const [savingOfficer, setSavingOfficer] = useState(false);

  /* ---------- API helpers ---------- */
  const fetchProfile = useCallback(async (sliitid) => {
    if (!sliitid) return null;
    const res = await fetch(`${ABSOLUTE_HOST}/Backend/get_profile.php`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ SLIITID: sliitid }),
    });
    const data = await toJsonSafe(res);
    if (data?.success && data.profile) {
      const rawImg = data.profile.image || data.profile.ProfileLink || "";
      return { name: data.profile.name || "", image: rawImg ? absImg(rawImg) : null, SLIITID: sliitid };
    }
    return null;
  }, []);

  /* ---------- load project ---------- */
  useEffect(() => {
    let cancelled = false;
    if (!projectId) { setLoading(false); setProject(null); return; }

    (async () => {
      try {
        setErr(""); setLoading(true);
        const res = await fetch(`${ABSOLUTE_HOST}/Backend/project_api.php?action=get_details&id=${projectId}`);
        const data = await toJsonSafe(res);
        if (cancelled) return;

        if (!data?.success) throw new Error(data?.message || "Failed to load project");

        const p = data.project || {};
        setProject(p);
        setDescription(p.description || "");
        setNeeds(p.needs_assessment || "");

        // auto-fill officer: prefer existing officer; else creator
        const defaultOfficer = p.project_officer_sliitid || p.created_by_sliitid || "";
        setOfficerId(defaultOfficer);
        if (defaultOfficer) {
          const prof = await fetchProfile(defaultOfficer);
          if (!cancelled) setOfficer(prof);
        }

        // annexures
        const filled = Array.from({ length: 12 }, (_, i) => {
          const found = (data.annexures || []).find(a => Number(a.slot_number) === i + 1);
          return { slot: i + 1, caption: found?.caption || "", url: found?.url || "" };
        });
        setAnnexures(filled);

        // team
        const nextTeam = ROLES.reduce((acc, role) => ({ ...acc, [role]: { sliitid: "", preview: null } }), {});
        (data.team || []).forEach((t) => {
          if (ROLES.includes(t.role)) {
            nextTeam[t.role] = {
              sliitid: t.sliitid || "",
              preview: t.member ? {
                name: t.member.name || "",
                image: t.member.image ? (t.member.image.startsWith("http") ? t.member.image : absImg(t.member.image)) : null,
                SLIITID: t.sliitid || ""
              } : null
            };
          }
        });
        setTeam(nextTeam);
      } catch (e) {
        console.error(e);
        setErr("Failed to load project");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [projectId, fetchProfile]);

  /* ---------- create minimal ---------- */
  const [newName, setNewName] = useState("");

  const createProject = async () => {
    const name = newName.trim();
    if (!name) return alert("Please enter a project name.");
    try {
      const res = await fetch(`${ABSOLUTE_HOST}/Backend/project_api.php?action=create_minimal`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, created_by_sliitid: user?.SLIITID || "" }),
      });
      const data = await toJsonSafe(res);
      if (!data?.success) return alert(data?.message || "Create failed");
      setProjectId(Number(data.id));
    } catch (e) {
      console.error(e); alert("Create failed");
    }
  };

  /* ---------- saves ---------- */
  const saveMeta = async () => {
    try {
      setSavingMeta(true);
      const res = await fetch(`${ABSOLUTE_HOST}/Backend/project_api.php?action=update_meta`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: Number(projectId), description, needs_assessment: needs }),
      });
      const data = await toJsonSafe(res);
      if (!data?.success) alert(data?.message || "Save failed"); else alert("Saved!");
    } catch {
      alert("Save failed");
    } finally { setSavingMeta(false); }
  };

  const saveAnnexures = async () => {
    try {
      setSavingAnnex(true);
      const payload = annexures
        .filter(a => (a.caption && a.caption.trim()) || (a.url && a.url.trim()))
        .map(a => ({ slot_number: a.slot, caption: a.caption.trim(), url: a.url.trim() }));
      const res = await fetch(`${ABSOLUTE_HOST}/Backend/project_api.php?action=upsert_annexures`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ project_id: Number(projectId), annexures: payload }),
      });
      const data = await toJsonSafe(res);
      if (!data?.success) alert(data?.message || "Save failed"); else alert("Annexures saved!");
    } catch {
      alert("Save failed");
    } finally { setSavingAnnex(false); }
  };

  const saveTeam = async () => {
    if (!projectId) return alert("Create the project first.");
    try {
      setSavingTeam(true);
      const payload = {
        project_id: Number(projectId),
        actor_sliitid: user?.SLIITID || "",
        team: ROLES.reduce((acc, r) => ({ ...acc, [r]: team[r].sliitid || "" }), {}),
      };
      const res = await fetch(`${ABSOLUTE_HOST}/Backend/project_api.php?action=upsert_team`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await toJsonSafe(res);
      if (!data?.success) return alert(data?.message || "Save failed");

      const next = ROLES.reduce((acc, r) => ({ ...acc, [r]: { sliitid: "", preview: null } }), {});
      (data.team || []).forEach((t) => {
        if (ROLES.includes(t.role)) {
          next[t.role] = {
            sliitid: t.sliitid || "",
            preview: t.member ? {
              name: t.member.name || "",
              image: t.member.image ? (t.member.image.startsWith("http") ? t.member.image : absImg(t.member.image)) : null,
              SLIITID: t.sliitid || ""
            } : null
          };
        }
      });
      setTeam(next);
      alert("Team saved!");
    } catch (e) {
      console.error(e); alert("Save failed");
    } finally { setSavingTeam(false); }
  };

  /* ---------- officer handlers (tile) ---------- */
  const onOfficerIdChange = async (val) => {
    const id = (val || "").toUpperCase().trim();
    setOfficerId(id);
    if (!id) { setOfficer(null); return; }
    const prof = await fetchProfile(id);
    setOfficer(prof);
  };

  const saveOfficer = async () => {
    if (!projectId) return alert("Create the project first.");
    try {
      setSavingOfficer(true);
      const res = await fetch(`${ABSOLUTE_HOST}/Backend/project_api.php?action=update_officer`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ project_id: Number(projectId), officer_sliitid: officerId }),
      });
      const data = await toJsonSafe(res);
      if (!data?.success) return alert(data?.message || "Save failed");
      alert("Project Officer updated!");
    } catch (e) {
      console.error(e); alert("Save failed");
    } finally { setSavingOfficer(false); }
  };

  const exportWord = () => projectId && window.open(`${ABSOLUTE_HOST}/Backend/export_project_word.php?id=${projectId}`, "_blank");
  const exportPdf  = () => projectId && window.open(`${ABSOLUTE_HOST}/Backend/export_project_pdf.php?id=${projectId}`, "_blank");

  /* ---------- team inputs ---------- */
  const onRoleChange = (role, value) => {
    const sliitid = (value || "").toUpperCase().trim();
    setTeam((prev) => ({ ...prev, [role]: { sliitid, preview: prev[role].preview } }));
  };
  const lookupRole = async (role) => {
    const id = team[role].sliitid;
    if (!id) return;
    const p = await fetchProfile(id);
    setTeam((prev) => ({ ...prev, [role]: { sliitid: id, preview: p } }));
  };
  const clearRole = (role) => setTeam((prev) => ({ ...prev, [role]: { sliitid: "", preview: null } }));

  /* ================================ UI ================================ */
  // 👉 PHASE: append " - Phase X" after name (defaults to 1 if provided but falsy/invalid).
  const phaseNum =
    project && Number.isFinite(+project.phase_count) && +project.phase_count > 0
      ? +project.phase_count
      : (project ? 1 : null);
  const phaseLabel = phaseNum ? ` - Phase ${phaseNum}` : "";

  return (
    <div className="min-h-screen overflow-x-hidden">
      {/* Sidebar (supports toggle in your layout) */}
      <Sidebar />

      {/* Topbar (fixed; its own spacer keeps document flow correct) */}
      <Topbar user={{ name: user?.name || "Member", role: user?.role || "Member", image: user?.image || "/user.png" }} />

      {/* CONTENT: leave room for the fixed sidebar at lg+, center on very wide screens */}
      <main className="lg:ml-64 mt-14 ml-0 w-full px-3 sm:px-5 md:px-8 lg:px-10 py-6 md:py-10">
        <div className="mx-auto w-full max-w-screen-2xl">

          {/* Back + role-gated report links */}
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "var(--sp-3)",
              marginBottom: "var(--sp-5)",
            }}
          >
            <Link to="/club-projects" className="btn btn-ghost btn-sm">
              <FaArrowLeft size={12} aria-hidden="true" /> Back to Projects
            </Link>

            {projectId > 0 && (
              <div style={{ display: "flex", alignItems: "center", gap: "var(--sp-2)", flexWrap: "wrap" }}>
                {hasRole(user?.role, PROJECT_REPORT_MANAGER_ROLES) && (
                  <button
                    onClick={() => navigate(`/project/${projectId}/tresurer`)}
                    className="btn btn-ghost btn-sm"
                    title="Treasurer Report (detailed)"
                  >
                    <FaFileInvoiceDollar size={12} aria-hidden="true" /> Treasurer Report
                  </button>
                )}

                {hasRole(user?.role, PROJECT_REPORT_MANAGER_ROLES) && (
                  <button
                    onClick={() => navigate(`/project/${projectId}/secretary-report`)}
                    className="btn btn-ghost btn-sm"
                    title="Secretary Project Report"
                  >
                    <FaClipboardList size={12} aria-hidden="true" /> Secretary Report
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Create-first card */}
          {!projectId && (
            <div className="panel" style={{ padding: "var(--sp-5)", marginBottom: "var(--sp-6)" }}>
              <h2 style={{ fontSize: 18, fontWeight: 700, marginBottom: "var(--sp-3)" }}>Create Project</h2>

              <div style={{ display: "flex", flexWrap: "wrap", gap: "var(--sp-3)", alignItems: "flex-end" }}>
                <div className="field" style={{ flex: "1 1 260px", minWidth: 0, marginBottom: 0 }}>
                  <label className="field-label" htmlFor="new-project-name">Project name</label>
                  <input
                    id="new-project-name"
                    type="text"
                    placeholder="Project name"
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                  />
                </div>

                <button onClick={createProject} className="btn btn-primary" title="Create project">
                  Create
                </button>
              </div>

              <p className="field-hint" style={{ marginTop: "var(--sp-2)" }}>
                We’ll auto-fill Project Officer with the creator.
              </p>
            </div>
          )}

          {/* Header + Officer tile (stacks on mobile/tablet, 2-col split at 980px+) */}
          {projectId > 0 && (
            <div className="split" style={{ marginBottom: "var(--sp-6)" }}>
              {/* Header */}
              <div className="panel" style={{ padding: "var(--sp-5)" }}>
                {loading ? (
                  <div>
                    <div className="skeleton" style={{ height: 20, width: 220, marginBottom: 10 }} />
                    <div className="skeleton" style={{ height: 12, width: 300 }} />
                  </div>
                ) : err ? (
                  <div className="error-banner">
                    <FaExclamationTriangle size={13} aria-hidden="true" />
                    <span>{err}</span>
                  </div>
                ) : (
                  <>
                    {/* Project name + Phase + Status */}
                    <div
                      style={{
                        display: "flex",
                        flexWrap: "wrap",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: "var(--sp-2)",
                      }}
                    >
                      <h1
                        className="truncate"
                        title={`${project?.name || "Project"}${phaseLabel}`}
                        style={{ fontSize: 22, fontWeight: 700, minWidth: 0 }}
                      >
                        {(project?.name || "Project")}{phaseLabel}
                      </h1>

                      {project?.status ? (
                        <span className={`pill ${pillClassForStatus(project.status)}`}>
                          <span className="dot" /> {project.status}
                        </span>
                      ) : null}
                    </div>

                    {/* Meta */}
                    <div
                      style={{
                        marginTop: "var(--sp-3)",
                        display: "flex",
                        flexWrap: "wrap",
                        gap: "var(--sp-2)",
                        alignItems: "center",
                      }}
                    >
                      <span className="eyebrow">Category</span>
                      <span className="truncate" style={{ fontSize: 12.5, color: "var(--text-2)", maxWidth: 280 }}>
                        {project?.category_name || "—"}
                      </span>

                      {project?.approved_by ? (
                        <span className="pill pill-success">
                          <span className="dot" />
                          Approved by {project.approved_by}
                          {project.approved_at ? ` · ${project.approved_at.split(" ")[0]}` : ""}
                        </span>
                      ) : null}
                    </div>
                  </>
                )}
              </div>

              {/* Project Officer tile */}
              <div className="panel" style={{ padding: "var(--sp-5)" }}>
                <div className="eyebrow" style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: "var(--sp-3)" }}>
                  <FaUserTie size={11} aria-hidden="true" /> Project Officer
                </div>

                <div style={{ display: "flex", alignItems: "center", gap: "var(--sp-3)", flexWrap: "wrap" }}>
                  <UserAvatar
                    src={officer?.image}
                    alt={officer?.name ? `${officer.name}'s photo` : "Project officer"}
                    size={48}
                  />
                  <div style={{ minWidth: 0 }}>
                    <div className="truncate" title={officer?.name || "—"} style={{ fontSize: 14, fontWeight: 600 }}>
                      {officer?.name || "—"}
                    </div>
                    <div className="truncate num" title={officerId || ""} style={{ fontSize: 12, color: "var(--text-3)" }}>
                      {officerId || "—"}
                    </div>
                  </div>
                </div>

                {/* Compact input to change officer by SLIIT ID */}
                <div className="field" style={{ marginTop: "var(--sp-4)", marginBottom: 0 }}>
                  <label className="field-label" htmlFor="officer-sliit-id">SLIIT ID</label>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    <input
                      id="officer-sliit-id"
                      type="text"
                      value={officerId}
                      onChange={(e) => onOfficerIdChange(e.target.value)}
                      placeholder="e.g. IT21808784"
                      style={{ flex: "1 1 160px", minWidth: 0 }}
                    />
                    <button
                      onClick={saveOfficer}
                      disabled={savingOfficer}
                      className="btn btn-primary btn-sm"
                      title="Save officer"
                      style={{ flexShrink: 0 }}
                    >
                      {savingOfficer ? <span className="spinner" aria-hidden="true" /> : "Save"}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Main content (stacks on mobile/tablet; 2 cols at lg+) */}
          {projectId > 0 && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 lg:gap-6">
              {/* Description */}
              <section className="panel" style={{ padding: "var(--sp-5)" }}>
                <div className="field" style={{ marginBottom: 0 }}>
                  <label className="field-label" htmlFor="proj-description">Project Description</label>
                  <textarea
                    id="proj-description"
                    rows={6}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Describe your project..."
                  />
                </div>

                <div style={{ marginTop: "var(--sp-4)", display: "flex", flexWrap: "wrap", gap: "var(--sp-2)" }}>
                  <button onClick={saveMeta} disabled={savingMeta} className="btn btn-primary">
                    {savingMeta && <span className="spinner" aria-hidden="true" />}
                    {savingMeta ? "Saving…" : "Save"}
                  </button>

                  <button onClick={exportWord} className="btn btn-ghost" title="Download Word">
                    <FaFileWord size={12} aria-hidden="true" /> Download Word
                  </button>

                  <button onClick={exportPdf} className="btn btn-ghost" title="Download PDF">
                    <FaFilePdf size={12} aria-hidden="true" /> Download PDF
                  </button>
                </div>
              </section>

              {/* Needs Assessment */}
              <section className="panel" style={{ padding: "var(--sp-5)" }}>
                <div className="field" style={{ marginBottom: 0 }}>
                  <label className="field-label" htmlFor="proj-needs">Needs Assessment</label>
                  <textarea
                    id="proj-needs"
                    rows={6}
                    value={needs}
                    onChange={(e) => setNeeds(e.target.value)}
                    placeholder="What is the need / problem this project addresses?"
                  />
                </div>
              </section>

              {/* Team */}
              <section className="panel lg:col-span-2" style={{ padding: "var(--sp-5)" }}>
                <div className="section-head" style={{ padding: 0, marginBottom: "var(--sp-3)" }}>
                  <h2 style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <FaUsers size={13} aria-hidden="true" style={{ color: "var(--accent)" }} /> Project Team
                  </h2>
                </div>

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
                    gap: "var(--sp-3)",
                  }}
                >
                  {ROLES.map((role) => {
                    const entry = team[role];
                    return (
                      <div
                        key={role}
                        style={{
                          background: "var(--surface-2)",
                          border: "1px solid var(--border)",
                          borderRadius: "var(--radius-md)",
                          padding: "var(--sp-3)",
                        }}
                      >
                        <div className="eyebrow" style={{ marginBottom: 8 }}>{role}</div>

                        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                          <input
                            type="text"
                            aria-label={`${role} SLIIT ID`}
                            placeholder="SLIIT ID (e.g. IT21808784)"
                            value={entry.sliitid}
                            onChange={(e) => onRoleChange(role, e.target.value)}
                            style={{ flex: "1 1 140px", minWidth: 0 }}
                          />
                          <button
                            onClick={() => lookupRole(role)}
                            className="btn btn-ghost btn-sm"
                            title="Lookup"
                            style={{ flexShrink: 0 }}
                          >
                            Lookup
                          </button>
                          <button
                            onClick={() => clearRole(role)}
                            className="btn btn-ghost btn-sm"
                            title="Clear"
                            style={{ flexShrink: 0 }}
                          >
                            Clear
                          </button>
                        </div>

                        {entry.preview && (
                          <div style={{ marginTop: 10, display: "flex", alignItems: "center", gap: 8 }}>
                            <UserAvatar
                              src={entry.preview.image}
                              alt={entry.preview.name ? `${entry.preview.name}'s photo` : `${role} photo`}
                              size={32}
                            />
                            <div style={{ minWidth: 0 }}>
                              <div className="truncate" style={{ fontSize: 13, fontWeight: 600 }}>
                                {entry.preview.name || entry.preview.SLIITID}
                              </div>
                              <div className="truncate num" style={{ fontSize: 11, color: "var(--text-3)" }}>
                                {entry.preview.SLIITID}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                <div style={{ marginTop: "var(--sp-4)" }}>
                  <button onClick={saveTeam} disabled={savingTeam} className="btn btn-primary">
                    {savingTeam && <span className="spinner" aria-hidden="true" />}
                    {savingTeam ? "Saving…" : "Save Team"}
                  </button>
                </div>
              </section>

              {/* Annexures */}
              <section className="panel lg:col-span-2" style={{ padding: "var(--sp-5)" }}>
                <div className="section-head" style={{ padding: 0, marginBottom: "var(--sp-3)" }}>
                  <h2 style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <FaPaperclip size={13} aria-hidden="true" style={{ color: "var(--accent)" }} /> Annexures
                  </h2>
                  <span className="eyebrow">Up to 12</span>
                </div>

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
                    gap: "var(--sp-3)",
                  }}
                >
                  {annexures.map((a, idx) => (
                    <div
                      key={a.slot}
                      style={{
                        background: "var(--surface-2)",
                        border: "1px solid var(--border)",
                        borderRadius: "var(--radius-md)",
                        padding: "var(--sp-3)",
                      }}
                    >
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                        <span className="eyebrow">Slot {a.slot}</span>
                        {a.url && a.url.trim() ? (
                          <a
                            href={a.url}
                            target="_blank"
                            rel="noreferrer"
                            className="icon-btn"
                            aria-label={`Open annexure ${a.slot} link in a new tab`}
                            title="Open link"
                          >
                            <FaExternalLinkAlt size={12} />
                          </a>
                        ) : null}
                      </div>

                      <input
                        type="text"
                        aria-label={`Caption for annexure slot ${a.slot}`}
                        placeholder="Caption"
                        value={a.caption}
                        onChange={(e) => {
                          const n = [...annexures];
                          n[idx] = { ...n[idx], caption: e.target.value };
                          setAnnexures(n);
                        }}
                        style={{ marginBottom: 8 }}
                      />

                      <input
                        type="url"
                        aria-label={`Link URL for annexure slot ${a.slot}`}
                        placeholder="https://link"
                        value={a.url}
                        onChange={(e) => {
                          const n = [...annexures];
                          n[idx] = { ...n[idx], url: e.target.value };
                          setAnnexures(n);
                        }}
                      />
                    </div>
                  ))}
                </div>

                <div style={{ marginTop: "var(--sp-4)" }}>
                  <button onClick={saveAnnexures} disabled={savingAnnex} className="btn btn-primary">
                    {savingAnnex && <span className="spinner" aria-hidden="true" />}
                    {savingAnnex ? "Saving…" : "Save Annexures"}
                  </button>
                </div>
              </section>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
